import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import QRCode from 'qrcode';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ContextMenu from '../components/ContextMenu';
import QRCodeModal from '../components/QRCodeModal';
import DeleteReceiptModal from '../components/DeleteReceiptModal';
import EditNoteModal from '../components/EditNoteModal';
import ItemDetailModal from '../components/ItemDetailModal';
import { trackEvent } from '../utils/posthog';
import '../App.css';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Receipt {
    receipt_id: string;
    merchant: string;
    datetime: string;
    total_gross: number;
    currency: string;
    unit_address: string | null;
    org_address: string | null;
    ico: string | null;
    dic: string | null;
    cash_register_code: string | null;
    invoice_number: string | null;
    receipt_number: string | null;
    receipt_hash: string | null;
    lat: number | null;
    lon: number | null;
    source: string;
    status: string;
    has_split: boolean;
    own_total_gross: number | null;
    created_at: string;
    note: string | null;
}

interface Item {
    item_id: string;
    receipt_id: string;
    raw_name: string;
    qty: number | null;
    unit_price: number | null;
    total_gross: number | null;
    vat_rate: number | null;
    vat_excl: number | null;
    vat_sum: number | null;
    cat_ai: string | null;
    category_id: string | null;
    category_path: string | null;
    category_source: string | null;
    parent_item_id: string | null;
    discounted: boolean;
    adjusted_total_gross: number | null;
    pack_qty: number | null;
    pack_unit: string | null;
    std_qty: number | null;
    std_unit: string | null;
    price_per_100: number | null;
    price_adj_per_100: number | null;
}

interface VATBreakdownItem {
    rate: number;
    exclVAT: number;
    vatAmount: number;
}

interface CategoryBreakdownItem {
    category: string;
    total: number;
}

interface SplitParticipant {
    person_order: number;
    person_name: string;
    person_email: string;
    total_amount: number;
    is_payer: boolean;
    is_contact: boolean;
    is_me: boolean;
    items: {
        item_id: string;
        raw_name: string;
        qty_share: number;
        unit_price: number;
        amount_share: number;
    }[];
}

interface SplitData {
    token?: string;
    status?: string;
    participants?: SplitParticipant[];
    payer_name: string | null;
    contact_name: string | null;
    can_identify_my_part?: boolean;
    // Received split fields
    is_received_split?: boolean;
    splitter_name?: string | null;
    source_token?: string | null;
}

interface ReceiptDetailResponse {
    receipt: Receipt;
    items: Item[];
    vatBreakdown: VATBreakdownItem[];
    categoryBreakdown: {
        level1: CategoryBreakdownItem[];
        level2: CategoryBreakdownItem[];
        level3: CategoryBreakdownItem[];
    };
    splitData: SplitData | null;
}

type SortField = 'raw_name' | 'qty' | 'unit_price' | 'total_gross';
type SortOrder = 'asc' | 'desc';

const ReceiptDetailPage: React.FC = () => {
    const { receiptId } = useParams<{ receiptId: string }>();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ReceiptDetailResponse | null>(null);
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [showQRModal, setShowQRModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditNoteModal, setShowEditNoteModal] = useState(false);
    const [showEditSplitOptionsModal, setShowEditSplitOptionsModal] = useState(false);
    const [showRemoveReceivedSplitModal, setShowRemoveReceivedSplitModal] = useState(false);
    const [showAllItems, setShowAllItems] = useState(false);
    const [sortField, setSortField] = useState<SortField>('raw_name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [viewMode, setViewMode] = useState<'my_part' | 'full_bill'>('my_part'); // Toggle between views
    const [expandedParticipant, setExpandedParticipant] = useState<number | null>(null); // For split summary expansion by person_order
    const [categoryLevel, setCategoryLevel] = useState<1 | 2 | 3>(2); // Category visualization level (default: level 2)
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to refresh receipt data

    useEffect(() => {
        const fetchReceiptDetail = async () => {
            try {
                setLoading(true);
                setError(null);

                const token = await getAuthToken(instance);
                if (!token) {
                    throw new Error('Nie ste prihlásený');
                }

                const response = await fetchWithRetry(
                    `${config.apiUrl}/getReceiptDetail?receipt_id=${receiptId}`,
                    {
                        method: 'GET',
                        headers: {
                            'X-Auth-Token': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Nepodarilo sa načítať detail bločku');
                }

                const responseData: ReceiptDetailResponse = await response.json();
                setData(responseData);

                // Update selectedItem if it's currently open (to reflect category changes)
                if (selectedItem) {
                    const updatedItem = responseData.items.find(item => item.item_id === selectedItem.item_id);
                    if (updatedItem) {
                        setSelectedItem(updatedItem);
                    }
                }

                // Generate QR code if receipt_hash exists
                if (responseData.receipt.receipt_hash) {
                    const qrUrl = await QRCode.toDataURL(responseData.receipt.receipt_hash);
                    setQrCodeUrl(qrUrl);
                }

                // Track receipt detail view
                trackEvent('receipt_detail_viewed', {
                    receipt_id: receiptId,
                    merchant: responseData.receipt.merchant,
                    total_amount: responseData.receipt.total_gross,
                    currency: responseData.receipt.currency,
                    items_count: responseData.items.length,
                    has_split: responseData.receipt.has_split,
                    source: responseData.receipt.source
                });
            } catch (err: any) {
                console.error('Error fetching receipt detail:', err);
                setError(err.message || 'Nepodarilo sa načítať detail bločku');
            } finally {
                setLoading(false);
            }
        };

        if (receiptId) {
            fetchReceiptDetail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiptId, refreshTrigger]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleSaveNote = async (note: string) => {
        try {
            const token = await getAuthToken(instance);
            if (!token) {
                throw new Error('Nie ste prihlásený');
            }

            const response = await fetchWithRetry(`${config.apiUrl}/saveReceiptNote`, {
                method: 'POST',
                headers: {
                    'X-Auth-Token': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    receipt_id: receiptId,
                    note: note,
                }),
            });

            if (!response.ok) {
                throw new Error('Nepodarilo sa uložiť poznámku');
            }

            // Update local state
            if (data) {
                setData({
                    ...data,
                    receipt: {
                        ...data.receipt,
                        note: note,
                    },
                });
            }

            setShowEditNoteModal(false);
        } catch (err: any) {
            console.error('Error saving note:', err);
            alert('Nepodarilo sa uložiť poznámku: ' + err.message);
        }
    };

    const handleDeleteReceipt = async (deleteSplit: boolean) => {
        try {
            const token = await getAuthToken(instance);
            if (!token) {
                throw new Error('Nie ste prihlásený');
            }

            const response = await fetchWithRetry(`${config.apiUrl}/deleteReceipt`, {
                method: 'DELETE',
                headers: {
                    'X-Auth-Token': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    receipt_id: receiptId,
                    delete_split: deleteSplit,
                }),
            });

            if (!response.ok) {
                throw new Error('Nepodarilo sa vymazať bloček');
            }

            // Navigate back to receipts list
            navigate('/receipts');
        } catch (err: any) {
            console.error('Error deleting receipt:', err);
            alert('Nepodarilo sa vymazať bloček: ' + err.message);
            setShowDeleteModal(false);
        }
    };

    // Filter out child discount items (parent_item_id !== null)
    const parentItems = data?.items ? data.items.filter(item => !item.parent_item_id) : [];

    // Check if this is a received split
    const isReceivedSplit = data?.splitData?.is_received_split || false;

    // Get canIdentifyMyPart from split data (not applicable for received splits - they always show only user's items)
    const canIdentifyMyPart = isReceivedSplit ? false : (data?.splitData?.can_identify_my_part || false);

    // Get user's items from split data if in "my_part" view
    const myItemIds = data?.splitData?.participants
        ?.find(p => p.is_me)?.items.map(item => item.item_id) || [];

    // Filter items based on view mode
    // For received splits: always show all items (they are already filtered to user's items)
    const viewFilteredItems = !isReceivedSplit && data?.receipt?.has_split && canIdentifyMyPart && viewMode === 'my_part'
        ? parentItems.filter(item => myItemIds.includes(item.item_id))
        : parentItems;

    const sortedItems = viewFilteredItems.length > 0 ? [...viewFilteredItems].sort((a, b) => {
        let aVal: any = a[sortField];
        let bVal: any = b[sortField];

        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }

        if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
    }) : [];

    const displayedItems = showAllItems ? sortedItems : sortedItems.slice(0, 3);

    // Format datetime (already in local time from eKasa, just format it)
    const formatDateTime = (dateStr: string) => {
        // Parse the datetime string directly without timezone conversion
        const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
            const [, year, month, day, hours, minutes, seconds] = match;
            return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
        }
        // Fallback if format is unexpected
        const date = new Date(dateStr);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${d}.${m}.${y} ${h}:${min}:${s}`;
    };

    // Get currency symbol
    const getCurrencySymbol = (currency: string) => {
        const symbols: { [key: string]: string } = {
            'EUR': '€',
            'USD': '$',
            'GBP': '£',
            'CZK': 'Kč',
            'PLN': 'zł'
        };
        return symbols[currency] || currency;
    };

    // Compute category breakdown from view-filtered items (respects my_part/full toggle)
    const computeCategoryBreakdown = (items: Item[], level: number): CategoryBreakdownItem[] => {
        const breakdown: { [key: string]: number } = {};
        const productItems = items.filter(item => {
            const itemCategory = (item as any).category;
            return itemCategory !== 'discount_item' && itemCategory !== 'discount_global';
        });

        productItems.forEach(item => {
            const amount = item.total_gross || 0;
            let pathNames: string[] = [];

            if (item.category_path) {
                pathNames = item.category_path.split(' > ').map(s => s.trim().replace(/\s*\(nutná manuálna kontrola\)/i, ''));
            } else if (item.cat_ai) {
                pathNames = [item.cat_ai];
            }

            let key: string;
            if (level === 1) {
                key = pathNames[0] || 'Nezaradené';
            } else if (level === 2) {
                key = pathNames[1] || pathNames[0] || 'Nezaradené';
            } else {
                key = pathNames.length > 0 ? pathNames[pathNames.length - 1] : 'Nezaradené';
            }

            breakdown[key] = (breakdown[key] || 0) + amount;
        });

        return Object.entries(breakdown)
            .map(([category, total]) => ({ category, total }))
            .sort((a, b) => b.total - a.total);
    };

    const categoryBreakdownArray = computeCategoryBreakdown(viewFilteredItems, categoryLevel);

    // Check if categorization is in progress (most items lack category_id)
    // Only show loading if there ARE items to categorize
    const uncategorizedCount = (data?.items || []).filter(item => !item.category_id).length;
    const totalProductItems = (data?.items || []).filter(item => {
        const itemCategory = (item as any).category;
        return itemCategory !== 'discount_item' && itemCategory !== 'discount_global';
    }).length;
    const hasProductItems = totalProductItems > 0;
    const categorizationInProgress = hasProductItems && (uncategorizedCount / totalProductItems) > 0.5;

    // Calculate VAT breakdown from view-filtered items
    const viewVatBreakdown = viewFilteredItems.reduce((acc: Record<number, { exclVAT: number; vatAmount: number }>, item) => {
        const rate = item.vat_rate ?? 0;
        if (!acc[rate]) {
            acc[rate] = { exclVAT: 0, vatAmount: 0 };
        }
        acc[rate].exclVAT += item.vat_excl ?? 0;
        acc[rate].vatAmount += item.vat_sum ?? 0;
        return acc;
    }, {});

    const viewVatBreakdownArray = Object.entries(viewVatBreakdown)
        .map(([rate, data]) => ({ rate: parseFloat(rate), ...data }))
        .sort((a, b) => b.rate - a.rate);

    // Prepare pie chart data
    const pieChartData = categoryBreakdownArray.length > 0 ? {
        labels: categoryBreakdownArray.map(c => c.category),
        datasets: [
            {
                data: categoryBreakdownArray.map(c => c.total),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#E7E9ED',
                    '#8DD3C7',
                ],
                hoverBackgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#E7E9ED',
                    '#8DD3C7',
                ],
            },
        ],
    } : null;

    const chartColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#E7E9ED', '#8DD3C7'];

    // Build category color map for ItemDetailModal
    const categoryColorMap: { [category: string]: string } = {};
    categoryBreakdownArray.forEach((cat, index) => {
        categoryColorMap[cat.category] = chartColors[index % chartColors.length];
    });

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>⏳ Načítavam detail bločku...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <p style={{ color: 'red' }}>❌ {error}</p>
                <button onClick={() => navigate('/receipts')} style={{ marginTop: '20px' }}>
                    ← Späť na zoznam
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: '20px' }}>
                <p>📭 Nenašli sa žiadne údaje</p>
            </div>
        );
    }

    const { receipt, items, vatBreakdown } = data;

    const handleStartOrEditSplit = async () => {
        // If split already exists, show options modal
        if (receipt.has_split) {
            setShowEditSplitOptionsModal(true);
            return;
        }

        // Otherwise, start new split
        await startSplitSession();
    };

    const startSplitSession = async (targetStep?: 'people' | 'split') => {
        try {
            const token = await getAuthToken(instance);
            if (!token) {
                throw new Error('Nie ste prihlásený');
            }

            const response = await fetchWithRetry(`${config.apiUrl}/startSplit`, {
                method: 'POST',
                headers: {
                    'X-Auth-Token': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    receipt_id: receiptId,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Nepodarilo sa spustiť rozdelenie');
            }

            const result = await response.json();
            // Navigate to add page with split token in state
            navigate('/add', {
                state: {
                    splitToken: result.token,
                    receiptId: receiptId,
                    fromReceiptDetail: true,
                    targetStep: targetStep || 'people'
                }
            });
        } catch (err: any) {
            console.error('Error starting split:', err);
            alert('Nepodarilo sa spustiť rozdelenie: ' + err.message);
        }
    };

    const handleRemoveReceivedSplit = async () => {
        setShowRemoveReceivedSplitModal(false);
        try {
            const token = await getAuthToken(instance);
            if (!token) throw new Error('Nie ste prihlásený');

            const response = await fetchWithRetry(`${config.apiUrl}/removeReceivedSplit`, {
                method: 'POST',
                headers: {
                    'X-Auth-Token': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ receipt_id: receiptId }),
            });

            if (!response.ok) throw new Error('Nepodarilo sa odstrániť bloček');

            navigate('/receipts');
        } catch (err: any) {
            console.error('Error removing received split:', err);
            alert('Nepodarilo sa odstrániť bloček: ' + err.message);
        }
    };

    const contextMenuItems = isReceivedSplit
        ? [
            {
                label: 'Odstrániť z môjho zoznamu',
                onClick: () => setShowRemoveReceivedSplitModal(true),
                color: '#e74c3c',
            },
        ]
        : [
            {
                label: 'Vygeneruj bloček ako PDF',
                onClick: () => {
                    // TODO: Implement PDF generation
                    alert('Generovanie PDF bude čoskoro k dispozícii');
                },
                disabled: true,
            },
            {
                label: 'Zobraz QR kód bločka',
                onClick: () => setShowQRModal(true),
                disabled: !receipt.receipt_hash,
            },
            {
                label: 'Upraviť poznámku',
                onClick: () => setShowEditNoteModal(true),
            },
            {
                label: receipt.has_split ? 'Upraviť rozdelenie' : 'Rozdeliť bloček',
                onClick: handleStartOrEditSplit,
            },
            {
                label: 'Vymazať bloček',
                onClick: () => setShowDeleteModal(true),
                color: '#e74c3c',
            },
        ];

    return (
        <div style={{ maxWidth: '100%', padding: '20px', backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
            {/* Back button and context menu */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                    onClick={() => navigate('/receipts')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#667eea',
                        fontSize: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}
                >
                    ← Späť na zoznam
                </button>
                <ContextMenu items={contextMenuItems} />
            </div>

            {/* Header Section - Merchant Info */}
            <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Názov obchodníka:</strong> {receipt.merchant}
                </div>
                {receipt.unit_address && (
                    <div style={{ marginBottom: '10px' }}>
                        <strong>Adresa predajne:</strong> {receipt.unit_address}
                    </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                    <strong>Dátum a čas nákupu:</strong> {formatDateTime(receipt.datetime)}
                </div>
                {receipt.note && (
                    <div style={{ marginTop: '15px' }}>
                        <strong>Vaša poznámka:</strong> {receipt.note}
                    </div>
                )}
            </div>

            {/* Split Notice - Different content for received vs own splits */}
            {receipt.has_split && isReceivedSplit && data?.splitData && (
                <div style={{
                    backgroundColor: '#f0f9ff',
                    border: '2px solid #bae6fd',
                    padding: '16px',
                    marginBottom: '20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                }}>
                    <span style={{ fontSize: '24px' }}>👥</span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#075985', lineHeight: '1.6', marginBottom: '8px' }}>
                            Toto je vám priradená časť bločka zo spoločného nákupu/platenia. Ak si ho neprajete zobrazovať vo svojom účte, kliknite vpravo hore na kontextové menu (tri bodky) a bloček odstránite.
                        </div>
                        <div style={{ fontSize: '13px', color: '#075985', lineHeight: '1.6' }}>
                            {data.splitData.payer_name && (
                                <div><strong>Platil:</strong> {data.splitData.payer_name}</div>
                            )}
                            {data.splitData.contact_name && (
                                <div><strong>Kontakt pre otázky:</strong> {data.splitData.contact_name}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {receipt.has_split && !isReceivedSplit && (
                <div style={{
                    backgroundColor: '#f0f9ff',
                    border: '2px solid #bae6fd',
                    padding: '16px',
                    marginBottom: '20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <span style={{ fontSize: '24px' }}>👥</span>
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#0369a1', marginBottom: '4px' }}>
                            Tento bloček bol rozdelený
                        </div>
                        <div style={{ fontSize: '13px', color: '#075985' }}>
                            {canIdentifyMyPart
                                ? 'Prepni si, či chceš vidieť iba tvoju časť bločka, alebo celý bloček. Nižšie nájdeš aj detail rozdelenia.'
                                : 'Nižšie nájdeš detail rozdelenia tohto bločka.'
                            }
                        </div>
                    </div>
                </div>
            )}

            {/* Tab Switcher - Only show if split exists AND we know user's part AND not a received split */}
            {receipt.has_split && canIdentifyMyPart && !isReceivedSplit && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '8px',
                    marginBottom: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: '8px'
                }}>
                    <button
                        onClick={() => setViewMode('my_part')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: viewMode === 'my_part' ? '#667eea' : 'transparent',
                            color: viewMode === 'my_part' ? '#fff' : '#6b7280',
                            transition: 'all 0.2s'
                        }}
                    >
                        Moja časť
                    </button>
                    <button
                        onClick={() => setViewMode('full_bill')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: viewMode === 'full_bill' ? '#667eea' : 'transparent',
                            color: viewMode === 'full_bill' ? '#fff' : '#6b7280',
                            transition: 'all 0.2s'
                        }}
                    >
                        Celý bloček
                    </button>
                </div>
            )}

            {/* Total Amount Section - Dynamic based on toggle */}
            <div style={{
                backgroundColor: 'white',
                padding: '20px',
                marginBottom: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                textAlign: 'center'
            }}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                    {isReceivedSplit ? 'Tvoja časť' : (receipt.has_split && canIdentifyMyPart && viewMode === 'my_part' ? 'Tvoja časť' : 'Celková suma')}
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>
                    {/* Show user's share for received splits or "my_part" view, otherwise show full total */}
                    {isReceivedSplit
                        ? (receipt.own_total_gross?.toFixed(2) || receipt.total_gross.toFixed(2))
                        : (receipt.has_split && canIdentifyMyPart && viewMode === 'my_part'
                            ? (data?.splitData?.participants?.find(p => p.is_me)?.total_amount?.toFixed(2) || '0.00')
                            : receipt.total_gross.toFixed(2))
                    } {getCurrencySymbol(receipt.currency)}
                </div>
            </div>

            {/* Items Section */}
            <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                    Položky
                    {receipt.has_split && canIdentifyMyPart && (
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', marginLeft: '8px' }}>
                            ({viewMode === 'my_part' ? 'Moja časť' : 'Celý bloček'})
                        </span>
                    )}
                </h3>

                {items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        📋 Tento bloček nemá žiadne položky. Alebo sa položky nepodarilo načítať.
                    </div>
                ) : (
                    <>
                        {/* Sort headers */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr auto',
                            padding: '10px 0',
                            borderBottom: '2px solid #eee',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <span
                                    onClick={() => handleSort('raw_name')}
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                    Názov {sortField === 'raw_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                </span>
                                <span style={{ fontSize: '12px', color: '#666' }}>
                                    Počet × Cena za kus
                                </span>
                            </div>
                            <div
                                onClick={() => handleSort('total_gross')}
                                style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                            >
                                Celková cena {sortField === 'total_gross' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </div>
                            <div style={{ width: '20px' }}></div>
                        </div>

                        {/* Items list */}
                        {displayedItems.map((item) => (
                            <div
                                key={item.item_id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr auto',
                                    padding: '15px 0',
                                    borderBottom: '1px solid #f0f0f0',
                                    cursor: 'pointer',
                                    alignItems: 'center'
                                }}
                                onClick={() => {
                                    setSelectedItem(item);
                                    // Track item detail view
                                    trackEvent('item_detail_viewed', {
                                        has_price_per_100: !!item.price_per_100,
                                        has_discount: item.discounted,
                                        has_category: !!item.cat_ai
                                    });
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <span style={{ fontSize: '14px' }}>
                                        {item.discounted && '🏷️ '}
                                        {item.raw_name}
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        {item.qty ?? 0} × {item.unit_price ? item.unit_price.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                    </span>
                                    {item.category_path && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                                            {item.category_path}
                                        </span>
                                    )}
                                    {!item.category_path && item.cat_ai && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                                            {item.cat_ai}
                                        </span>
                                    )}
                                    {!item.category_path && !item.cat_ai && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                                            Kategorizuje sa...
                                        </span>
                                    )}
                                </div>
                                <div style={{
                                    textAlign: 'right',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    gap: '5px'
                                }}>
                                    {item.discounted ? (
                                        <>
                                            <span style={{ fontSize: '14px', color: '#999', textDecoration: 'line-through' }}>
                                                {item.total_gross ? item.total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                            </span>
                                            <span style={{ color: '#e74c3c' }}>
                                                {item.adjusted_total_gross ? item.adjusted_total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                            </span>
                                        </>
                                    ) : (
                                        <span style={{ color: (item.total_gross ?? 0) < 0 ? '#e74c3c' : '#2c3e50' }}>
                                            {item.total_gross ? item.total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                        </span>
                                    )}
                                </div>
                                <div style={{ paddingLeft: '10px', color: '#999', fontSize: '18px' }}>
                                    ›
                                </div>
                            </div>
                        ))}

                        {/* Show more/less button */}
                        {sortedItems.length > 3 && (
                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button
                                    onClick={() => {
                                        setShowAllItems(!showAllItems);
                                        // Track items list expansion
                                        if (!showAllItems) {
                                            trackEvent('items_list_expanded', {
                                                total_items: sortedItems.length,
                                                displayed_items: 3
                                            });
                                        }
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#667eea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    {showAllItems ? `Skryť ${sortedItems.length - 3} položiek` : `Zobraziť ďalších ${sortedItems.length - 3} položiek`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Category Breakdown - Show loading state if categorization in progress */}
            {categorizationInProgress ? (
                <div style={{ backgroundColor: 'white', padding: '40px 20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 2s ease-in-out infinite' }}>
                        🏷️
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                        Prebieha kategorizácia...
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                        Naša AI práve zaraďuje položky do kategórií. Trvá to len chvíľu.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            background: '#667eea',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        🔄 Obnoviť stránku
                    </button>
                    <style>{`
                        @keyframes pulse {
                            0%, 100% { opacity: 1; transform: scale(1); }
                            50% { opacity: 0.7; transform: scale(1.05); }
                        }
                    `}</style>
                </div>
            ) : pieChartData && categoryBreakdownArray.length > 0 && (
                <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>
                            Kategórie položiek
                            {receipt.has_split && canIdentifyMyPart && (
                                <span style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', marginLeft: '8px' }}>
                                    ({viewMode === 'my_part' ? 'Moja časť' : 'Celý bloček'})
                                </span>
                            )}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px' }}>
                            <button
                                onClick={() => setCategoryLevel(Math.max(1, categoryLevel - 1) as 1 | 2 | 3)}
                                disabled={categoryLevel === 1}
                                style={{
                                    padding: '6px 10px',
                                    backgroundColor: categoryLevel === 1 ? '#e5e7eb' : '#667eea',
                                    color: categoryLevel === 1 ? '#9ca3af' : 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: categoryLevel === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                ◀
                            </button>
                            <span style={{ fontWeight: 500, minWidth: '80px', textAlign: 'center' }}>
                                Úroveň {categoryLevel}
                            </span>
                            <button
                                onClick={() => setCategoryLevel(Math.min(3, categoryLevel + 1) as 1 | 2 | 3)}
                                disabled={categoryLevel === 3}
                                style={{
                                    padding: '6px 10px',
                                    backgroundColor: categoryLevel === 3 ? '#e5e7eb' : '#667eea',
                                    color: categoryLevel === 3 ? '#9ca3af' : 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: categoryLevel === 3 ? 'not-allowed' : 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                ▶
                            </button>
                        </div>
                    </div>
                    <div style={{ maxWidth: '400px', margin: '0 auto 20px' }}>
                        <Pie data={pieChartData} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {categoryBreakdownArray.map((cat, index) => (
                            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: chartColors[index % chartColors.length],
                                        borderRadius: '3px'
                                    }}></div>
                                    <span>{cat.category}</span>
                                </div>
                                <span style={{ fontWeight: 'bold', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {cat.total.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* VAT Breakdown Table */}
            {viewVatBreakdownArray.length > 0 && (
                <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                        Koľko si štát zobral z vášho nákupu (DPH)
                        {receipt.has_split && canIdentifyMyPart && (
                            <span style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', marginLeft: '8px' }}>
                                ({viewMode === 'my_part' ? 'Moja časť' : 'Celý bloček'})
                            </span>
                        )}
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #eee' }}>
                                <th style={{ textAlign: 'left', padding: '10px' }}>Sadzba DPH</th>
                                <th style={{ textAlign: 'right', padding: '10px' }}>Bez DPH</th>
                                <th style={{ textAlign: 'right', padding: '10px' }}>Zaplatená DPH</th>
                            </tr>
                        </thead>
                        <tbody>
                            {viewVatBreakdownArray.map((vat, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '10px' }}>
                                        {vat.rate % 1 === 0 ? vat.rate.toFixed(0) : vat.rate.toFixed(2)}%
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '10px' }}>
                                        {vat.exclVAT.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '10px' }}>
                                        {vat.vatAmount.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '15px', fontStyle: 'italic' }}>
                        Pri zaokrúhľovaní na dve desatinné miesta môže vzniknúť rozdiel +- 1c oproti vytlačenému pokladničnému dokladu.
                    </div>
                </div>
            )}

            {/* Split Summary Section - Only for own splits (not received splits) */}
            {receipt.has_split && !isReceivedSplit && (
                <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px' }}>👥 Rozdelenie medzi ľudí</h3>

                    {/* Real participants data from backend */}
                    {data?.splitData?.participants.map((person) => {
                        // Build display name with indicators
                        let displayName = person.person_name;
                        if (person.is_me) displayName = `Ty (${displayName})`;
                        if (person.is_payer) displayName += ' - Platil bloček';
                        if (person.is_contact) displayName += ' - Kontaktná osoba';

                        return (
                            <div key={person.person_order} style={{ marginBottom: '12px' }}>
                                <div
                                    onClick={() => setExpandedParticipant(expandedParticipant === person.person_order ? null : person.person_order)}
                                    style={{
                                        padding: '14px',
                                        backgroundColor: person.is_me ? '#f0f9ff' : '#f9fafb',
                                        border: person.is_me ? '2px solid #bae6fd' : '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                                            {displayName}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#667eea' }}>
                                            {person.total_amount.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                            {expandedParticipant === person.person_order ? '▲' : '▼'}
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded details - show items for this person */}
                                {expandedParticipant === person.person_order && (
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '12px',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>
                                            Položky:
                                        </div>
                                        {person.items.map((item, idx) => (
                                            <div key={item.item_id} style={{
                                                fontSize: '13px',
                                                color: '#374151',
                                                padding: '4px 0',
                                                borderBottom: idx < person.items.length - 1 ? '1px solid #e5e7eb' : 'none'
                                            }}>
                                                {item.raw_name} - {item.qty_share} × {item.unit_price.toFixed(2)} = {item.amount_share.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* CTA Buttons */}
                    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button
                            onClick={handleStartOrEditSplit}
                            style={{
                                padding: '14px 20px',
                                backgroundColor: '#667eea',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            {receipt.has_split ? 'Upraviť rozdelenie' : 'Rozdeliť bloček'}
                        </button>
                    </div>
                </div>
            )}

            {/* Show CTA button even if no split exists (not for received splits) */}
            {!receipt.has_split && !isReceivedSplit && (
                <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                    <button
                        onClick={handleStartOrEditSplit}
                        style={{
                            padding: '14px 24px',
                            backgroundColor: '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            width: '100%'
                        }}
                    >
                        Rozdeliť bloček
                    </button>
                </div>
            )}

            {/* Additional Receipt Info */}
            <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Ďalšie údaje o bločku</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {receipt.org_address && (
                        <div>
                            <strong>Adresa sídla predajcu:</strong> {receipt.org_address}
                        </div>
                    )}
                    {receipt.ico && (
                        <div>
                            <strong>IČO:</strong> {receipt.ico}
                        </div>
                    )}
                    {receipt.dic && (
                        <div>
                            <strong>DIČ:</strong> {receipt.dic}
                        </div>
                    )}
                    {receipt.cash_register_code && (
                        <div>
                            <strong>Číslo pokladnice:</strong> {receipt.cash_register_code}
                        </div>
                    )}
                    {(receipt.invoice_number || receipt.receipt_number) && (
                        <div>
                            <strong>Číslo pokladničného dokladu alebo faktúry:</strong> {receipt.invoice_number || receipt.receipt_number}
                        </div>
                    )}
                    {receipt.receipt_hash && !isReceivedSplit && (
                        <div>
                            <strong>UID bločka:</strong> {receipt.receipt_hash}
                        </div>
                    )}
                </div>
            </div>

            {/* Map Section */}
            <div style={{ backgroundColor: 'white', padding: '20px', marginBottom: '100px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Mapa</h3>
                {receipt.lat && receipt.lon ? (
                    <div style={{ width: '100%', height: '300px' }}>
                        <iframe
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            scrolling="no"
                            marginHeight={0}
                            marginWidth={0}
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${receipt.lon - 0.01},${receipt.lat - 0.01},${receipt.lon + 0.01},${receipt.lat + 0.01}&layer=mapnik&marker=${receipt.lat},${receipt.lon}`}
                            style={{ border: '1px solid #ddd', borderRadius: '5px' }}
                            title="Mapa predajne"
                        />
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        📍 Údaje o polohe predajne nie sú dostupné. Mrzí nás to.
                    </div>
                )}
            </div>

            {/* Modals */}
            {showQRModal && qrCodeUrl && (
                <QRCodeModal qrCodeUrl={qrCodeUrl} onClose={() => setShowQRModal(false)} />
            )}

            {showDeleteModal && (
                <DeleteReceiptModal
                    merchant={receipt.merchant}
                    datetime={formatDateTime(receipt.datetime)}
                    totalGross={receipt.total_gross}
                    currency={receipt.currency}
                    hasSplit={receipt.has_split}
                    onConfirm={handleDeleteReceipt}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}

            {showEditNoteModal && (
                <EditNoteModal
                    currentNote={receipt.note}
                    onSave={handleSaveNote}
                    onCancel={() => setShowEditNoteModal(false)}
                />
            )}

            {selectedItem && (
                <ItemDetailModal
                    item={selectedItem}
                    receipt={receipt}
                    allItems={items}
                    categoryColors={categoryColorMap}
                    onClose={() => setSelectedItem(null)}
                    getAuthToken={() => getAuthToken(instance)}
                    onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                />
            )}

            {/* Edit Split Options Modal */}
            {showEditSplitOptionsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }} onClick={() => setShowEditSplitOptionsModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px', color: '#111827', fontSize: '20px' }}>
                            Čo chcete upraviť?
                        </h3>
                        <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                            Vyberte, čo chcete zmeniť v rozdelení bločka:
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    setShowEditSplitOptionsModal(false);
                                    startSplitSession('split');
                                }}
                                style={{
                                    padding: '14px 16px',
                                    backgroundColor: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                <span style={{ fontSize: '20px' }}>↩️</span>
                                <div>
                                    <div>Upraviť len rozdelenie</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                                        Zmeniť počty položiek pre osoby
                                    </div>
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    setShowEditSplitOptionsModal(false);
                                    startSplitSession('people');
                                }}
                                style={{
                                    padding: '14px 16px',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                <span style={{ fontSize: '20px' }}>👥</span>
                                <div>
                                    <div>Zmeniť osoby</div>
                                    <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                                        Pridať/odobrať účastníkov
                                    </div>
                                </div>
                            </button>
                            <button
                                onClick={() => setShowEditSplitOptionsModal(false)}
                                style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#6b7280',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove received split confirmation modal */}
            {showRemoveReceivedSplitModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }} onClick={() => setShowRemoveReceivedSplitModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px', color: '#111827', fontSize: '20px' }}>
                            Odstrániť bloček
                        </h3>
                        <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                            Naozaj chceš odstrániť tento bloček z tvojho zoznamu?
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowRemoveReceivedSplitModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#6b7280',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Zrušiť
                            </button>
                            <button
                                onClick={handleRemoveReceivedSplit}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Odstrániť
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReceiptDetailPage;
